// Helpers for the global selector, shared by the dashboard (URL state) and the CLI (flags).

import type { FilterParams } from './types.ts';

/**
 * Dimension filters in the order of the global selector:
 * GAME → PLATFORM → COUNTRY → DATE RANGE → ACQUISITION SOURCE → CAMPAIGN → CREATIVE → APP VERSION → EXPERIMENT.
 * (game is part of the path; the date range sits between country and network.)
 */
export const DIMENSION_FILTERS = ['platform', 'country', 'network', 'campaign', 'creative', 'app_version', 'experiment'] as const;
export type DimensionFilter = (typeof DIMENSION_FILTERS)[number];

/** Every query key understood by the metrics endpoints. */
export const FILTER_KEYS = ['environment', 'from', 'to', 'last', ...DIMENSION_FILTERS, 'ad_group', 'include_internal'] as const;

const ISO = /^\d{4}-\d{2}-\d{2}$/;

export function isIsoDate(s: unknown): s is string {
  return typeof s === 'string' && ISO.test(s) && !Number.isNaN(Date.parse(`${s}T00:00:00Z`));
}

export function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Whole days from a to b (b - a). */
export function daysBetween(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
}

/** Today's date (YYYY-MM-DD) in an IANA timezone. */
export function todayIn(timeZone: string, now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** Resolves `last` / `from` / `to` the same way the server does (default: last `defaultDays` days). */
export function resolveRange(f: Pick<FilterParams, 'from' | 'to' | 'last'>, today: string, defaultDays = 30): { from: string; to: string; days: number } {
  let from = f.from;
  let to = f.to;
  const m = f.last ? /^(\d{1,3})d$/.exec(f.last) : null;
  if (m) {
    to = today;
    from = addDays(today, -(Number(m[1]) - 1));
  }
  to = to ?? today;
  from = from ?? addDays(to, -(defaultDays - 1));
  return { from, to, days: daysBetween(from, to) + 1 };
}

/** Same filters over the immediately preceding period of equal length (for "vs previous period"). */
export function previousPeriod(f: FilterParams, today: string, defaultDays = 30): FilterParams {
  const { from, days } = resolveRange(f, today, defaultDays);
  const { last: _last, ...rest } = f;
  return { ...rest, from: addDays(from, -days), to: addDays(from, -1) };
}

/** Keeps only known, non-empty filter keys (e.g. when reading URL search params). */
export function pickFilters(source: Record<string, string | null | undefined>): FilterParams {
  const out: Record<string, string | boolean> = {};
  for (const k of FILTER_KEYS) {
    const v = source[k];
    if (v === undefined || v === null || v === '') continue;
    out[k] = k === 'include_internal' ? v === 'true' || v === '1' : v;
  }
  return out as FilterParams;
}
